/**
 * FHIR AuditEvent → UI row mapping for the Audit Logs table.
 * Maps the full selectable field catalog, including sensitive fields
 * (entity.query, outcomeDesc, agent.network, entity.name/description/detail).
 */
import type {
  AuditEvent,
  AuditEventAgent,
  AuditEventEntity,
  CodeableConcept,
  Coding,
  Reference,
} from '@medplum/fhirtypes';
import { z } from 'zod';

const EMPTY = '—';
const ENTITY_CAP = 3;
const BASE64_DETAIL_MAX = 64;

export const auditLogRowSchema = z.object({
  id: z.string(),
  lastUpdated: z.string(),
  versionId: z.string(),
  profile: z.string(),
  security: z.string(),
  tag: z.string(),
  compartment: z.string(),
  project: z.string(),
  recorded: z.string(),
  /** Condensed event label, e.g. "Read" or "Execute". */
  eventDisplay: z.string(),
  type: z.string(),
  subtype: z.string(),
  action: z.string(),
  period: z.string(),
  outcome: z.string(),
  outcomeDesc: z.string(),
  purposeOfEvent: z.string(),
  agentDisplay: z.string(),
  agentWho: z.string(),
  agentName: z.string(),
  agentType: z.string(),
  agentRole: z.string(),
  agentAltId: z.string(),
  agentRequestor: z.string(),
  agentLocation: z.string(),
  agentPolicy: z.string(),
  agentMedia: z.string(),
  agentPurposeOfUse: z.string(),
  agentNetworkAddress: z.string(),
  agentNetworkType: z.string(),
  entitySummary: z.string(),
  entityWhat: z.string(),
  entityType: z.string(),
  entityRole: z.string(),
  entityLifecycle: z.string(),
  entitySecurityLabel: z.string(),
  entityName: z.string(),
  entityDescription: z.string(),
  entityQuery: z.string(),
  entityDetail: z.string(),
  sourceSite: z.string(),
  sourceObserver: z.string(),
  sourceType: z.string(),
});

export type AuditLogRow = z.infer<typeof auditLogRowSchema>;

const ACTION_LABELS: Record<string, string> = {
  C: 'Create',
  R: 'Read',
  U: 'Update',
  D: 'Delete',
  E: 'Execute',
};

/** FHIR AuditEventOutcome codes → display labels. */
export const OUTCOME_LABELS: Record<string, string> = {
  '0': 'Success',
  '4': 'Minor failure',
  '8': 'Serious failure',
  '12': 'Major failure',
};

function actionLabel(action: string | undefined): string | undefined {
  if (!action) return undefined;
  return ACTION_LABELS[action] ?? action;
}

function outcomeLabel(outcome: string | undefined): string | undefined {
  if (!outcome) return undefined;
  return OUTCOME_LABELS[outcome] ?? outcome;
}

function orEmpty(value: string | undefined | null): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : EMPTY;
}

function codingLabel(coding: Coding | undefined): string | undefined {
  if (!coding) return undefined;
  const label = coding.display || coding.code;
  return label || undefined;
}

function codeableConceptLabel(cc: CodeableConcept | undefined): string | undefined {
  if (!cc) return undefined;
  if (cc.text?.trim()) return cc.text.trim();
  return codingLabel(cc.coding?.[0]);
}

function referenceLabel(ref: Reference | undefined): string | undefined {
  if (!ref) return undefined;
  return ref.display || ref.reference || undefined;
}

function joinNonEmpty(parts: Array<string | undefined>, sep = ', '): string {
  const values = parts.map((p) => p?.trim()).filter((p): p is string => Boolean(p));
  return values.length > 0 ? values.join(sep) : EMPTY;
}

function formatCodingList(list: Coding[] | undefined): string {
  if (!list?.length) return EMPTY;
  return joinNonEmpty(list.map((c) => codingLabel(c)));
}

function formatCodeableList(list: CodeableConcept[] | undefined): string {
  if (!list?.length) return EMPTY;
  return joinNonEmpty(list.map((c) => codeableConceptLabel(c)));
}

function formatReferenceList(list: Reference[] | undefined): string {
  if (!list?.length) return EMPTY;
  return joinNonEmpty(list.map((r) => referenceLabel(r)));
}

function formatStringList(list: string[] | undefined): string {
  if (!list?.length) return EMPTY;
  return joinNonEmpty(list);
}

/** Short UI labels for common factory AuditEvent.type codes. */
function typeDisplayLabel(coding: Coding | undefined): string {
  if (!coding) return EMPTY;
  const key = (coding.code || coding.display || '').trim().toLowerCase();
  if (key === 'rest' || key === 'restful operation') return 'REST';
  if (key === 'execute') return 'Execute';
  return coding.display || coding.code || EMPTY;
}

function subtypeDisplayLabel(coding: Coding | undefined): string | undefined {
  return codingLabel(coding);
}

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** Single column label: action/subtype when present (e.g. "Read"), else type (e.g. "Execute"). */
export function eventDisplayLabel(event: AuditEvent): string {
  const type = typeDisplayLabel(event.type);
  const subtype = subtypeDisplayLabel(event.subtype?.[0]);
  const detail = actionLabel(event.action) ?? (subtype ? titleCase(subtype) : undefined);

  if (detail) return detail;
  return type;
}

function selectAgent(event: AuditEvent): AuditEventAgent | undefined {
  return event.agent?.find((a) => a.requestor) ?? event.agent?.[0];
}

function selectEntities(event: AuditEvent): AuditEventEntity[] {
  return event.entity?.slice(0, ENTITY_CAP) ?? [];
}

function joinEntityField(
  entities: AuditEventEntity[],
  pick: (entity: AuditEventEntity) => string | undefined,
): string {
  return joinNonEmpty(entities.map(pick));
}

function truncateBase64(value: string): string {
  if (value.length <= BASE64_DETAIL_MAX) return value;
  return `${value.slice(0, BASE64_DETAIL_MAX)}…`;
}

function serializeEntityDetail(entity: AuditEventEntity): string | undefined {
  if (!entity.detail?.length) return undefined;
  const pairs = entity.detail
    .map((d) => {
      const type = d.type?.trim() || 'detail';
      const value =
        d.valueString != null
          ? d.valueString
          : d.valueBase64Binary != null
            ? truncateBase64(d.valueBase64Binary)
            : undefined;
      if (!value) return undefined;
      return `${type}|${value}`;
    })
    .filter((p): p is string => Boolean(p));
  return pairs.length > 0 ? pairs.join('; ') : undefined;
}

function agentDisplayFrom(agent: AuditEventAgent | undefined): string {
  if (!agent) return EMPTY;
  const who = agent.who;
  if (who?.display) return who.display;
  if (who?.reference) return who.reference;
  if (agent.name) return agent.name;
  return EMPTY;
}

function formatPeriod(event: AuditEvent): string {
  const start = event.period?.start;
  const end = event.period?.end;
  if (!start && !end) return EMPTY;
  if (start && end) return `${start}–${end}`;
  return start || end || EMPTY;
}

export function mapAuditEventToRow(event: AuditEvent): AuditLogRow {
  const agent = selectAgent(event);
  const entities = selectEntities(event);
  const meta = event.meta;

  const row: AuditLogRow = {
    id: event.id ?? `${event.recorded}-${event.action ?? 'x'}`,
    lastUpdated: orEmpty(meta?.lastUpdated),
    versionId: orEmpty(meta?.versionId),
    profile: formatStringList(meta?.profile),
    security: formatCodingList(meta?.security),
    tag: formatCodingList(meta?.tag),
    compartment: formatReferenceList(meta?.compartment),
    project: orEmpty(meta?.project),
    recorded: event.recorded ?? '',
    eventDisplay: eventDisplayLabel(event),
    type: orEmpty(codingLabel(event.type) ?? typeDisplayLabel(event.type)),
    subtype: joinNonEmpty((event.subtype ?? []).map((c) => codingLabel(c))),
    action: orEmpty(actionLabel(event.action) ?? event.action),
    period: formatPeriod(event),
    outcome: orEmpty(outcomeLabel(event.outcome) ?? event.outcome),
    outcomeDesc: orEmpty(event.outcomeDesc),
    purposeOfEvent: formatCodeableList(event.purposeOfEvent),
    agentDisplay: agentDisplayFrom(agent),
    agentWho: orEmpty(agent?.who?.reference),
    agentName: orEmpty(agent?.name),
    agentType: orEmpty(codeableConceptLabel(agent?.type)),
    agentRole: formatCodeableList(agent?.role),
    agentAltId: orEmpty(agent?.altId),
    agentRequestor:
      agent?.requestor === undefined ? EMPTY : agent.requestor ? 'true' : 'false',
    agentLocation: orEmpty(referenceLabel(agent?.location)),
    agentPolicy: formatStringList(agent?.policy),
    agentMedia: orEmpty(codingLabel(agent?.media)),
    agentPurposeOfUse: formatCodeableList(agent?.purposeOfUse),
    agentNetworkAddress: orEmpty(agent?.network?.address),
    agentNetworkType: orEmpty(agent?.network?.type),
    entitySummary: joinEntityField(entities, (e) => e.what?.reference),
    entityWhat: joinEntityField(entities, (e) => e.what?.reference),
    entityType: joinEntityField(entities, (e) => codingLabel(e.type)),
    entityRole: joinEntityField(entities, (e) => codingLabel(e.role)),
    entityLifecycle: joinEntityField(entities, (e) => codingLabel(e.lifecycle)),
    entitySecurityLabel: joinEntityField(entities, (e) =>
      e.securityLabel?.length ? joinNonEmpty(e.securityLabel.map((c) => codingLabel(c))) : undefined,
    ),
    entityName: joinEntityField(entities, (e) => e.name),
    entityDescription: joinEntityField(entities, (e) => e.description),
    entityQuery: joinEntityField(entities, (e) => e.query),
    entityDetail: joinNonEmpty(entities.map((e) => serializeEntityDetail(e))),
    sourceSite: orEmpty(event.source?.site),
    sourceObserver: orEmpty(referenceLabel(event.source?.observer)),
    sourceType: formatCodingList(event.source?.type),
  };

  return auditLogRowSchema.parse(row);
}
